import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, IconButton, Collapse, Tooltip, CircularProgress } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useSelector } from "react-redux";
import InputBox, { type ChatSendPayload, type QueuedChatMessage } from "./InputBox";
import { buildTerminalLines } from "../utils/remoteSessionTerminalUtils";
import { chatApiInstance } from "../apis/chatApi";

const EMPTY_SSH_EVENTS: any[] = [];

export interface RemoteSessionInputSectionProps {
  inputMessage: string;
  setInputMessage: (msg: string) => void;
  chatIsLoading: boolean;
  aiThreadId: string | null;
  /** Required for disconnect; omit to hide/disable disconnect control. */
  userId?: string | null;
  handleSendMessage: (messageOverride?: string | ChatSendPayload) => void;
  queuedMessages?: QueuedChatMessage[];
  onDeleteQueuedMessage?: (id: string) => void;
  onUpdateQueuedMessage?: (id: string, content: string) => void;
  onSendQueuedMessageNow?: (id: string) => void;
  canSendQueuedMessagesNow?: boolean;
  onStopTurn?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  showTypeahead?: boolean;
  hasNavigatedToTypeahead?: boolean;
  isEmbedded?: boolean;
  fontSizes?: { input: number };
  currentMspId?: string | number | null;
  preloadedReportAttachments?: Array<{
    jobId: number;
    title: string;
    completedAt: string | null;
  }>;
  preloadForThreadId?: string | null;
  onPreloadedReportAttachmentsConsumed?: () => void;
  modeControl?: React.ReactNode;
  enableReportAttachments?: boolean;
  disabledReason?: string | null;
  /** See InputBox. */
  placeholder?: string | null;
}

/**
 * Input area with collapsible remote session (device agent) terminal drawer when Redux reports an active session.
 * Matches NeoAssistant chat-bubble UX for ticket chat and Chat Assistant.
 */
const RemoteSessionInputSection: React.FC<RemoteSessionInputSectionProps> = ({
  inputMessage,
  setInputMessage,
  chatIsLoading,
  aiThreadId,
  userId = null,
  handleSendMessage,
  queuedMessages = [],
  onDeleteQueuedMessage,
  onUpdateQueuedMessage,
  onSendQueuedMessageNow,
  canSendQueuedMessagesNow,
  onStopTurn,
  inputRef,
  showTypeahead = false,
  hasNavigatedToTypeahead = false,
  isEmbedded = false,
  fontSizes,
  currentMspId = null,
  preloadedReportAttachments = [],
  preloadForThreadId = null,
  onPreloadedReportAttachmentsConsumed,
  modeControl,
  enableReportAttachments,
  disabledReason,
  placeholder,
}) => {
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [hasRemoteSessionDrawer, setHasRemoteSessionDrawer] = useState(false);
  const [remoteSessionHeight, setRemoteSessionHeight] = useState(260);
  const [disconnectInFlight, setDisconnectInFlight] = useState(false);
  const resizeStartRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const terminalOutputRef = useRef<HTMLDivElement | null>(null);

  const threadKey = aiThreadId != null ? String(aiThreadId) : null;
  const remoteSession = useSelector((state: any) =>
    threadKey
      ? state.chat.remoteShellByThreadId?.[threadKey]?.remoteSession ?? null
      : null
  );
  const sshEvents = useSelector((state: any) =>
    threadKey
      ? state.chat.remoteShellByThreadId?.[threadKey]?.sshEvents ?? EMPTY_SSH_EVENTS
      : EMPTY_SSH_EVENTS
  );
  // Computer use is no longer drawn here. A browser run renders inline in the transcript at
  // the tool call that opened it, so that a thread shows *when* the browser was used; this
  // drawer is the remote-shell terminal and nothing else.
  useEffect(() => {
    const isActive = remoteSession?.active === true;
    console.debug("[RemoteSessionDrawer] session changed", remoteSession);
    setHasRemoteSessionDrawer(isActive);
    if (isActive) {
      console.debug("[RemoteSessionDrawer] Opening drawer from backend session event");
      setChatDrawerOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteSession]);

  const remoteSessionStatusText = useMemo(() => {
    if (!remoteSession) return "Inactive";
    if (remoteSession.status === "error") return "Error";
    if (remoteSession.active) return "Active";
    return "Closed";
  }, [remoteSession]);

  const terminalLines = useMemo(
    () => buildTerminalLines(sshEvents || []),
    [sshEvents]
  );

  useEffect(() => {
    if (!chatDrawerOpen) return;
    const terminalEl = terminalOutputRef.current;
    if (!terminalEl) return;

    requestAnimationFrame(() => {
      terminalEl.scrollTop = terminalEl.scrollHeight;
    });
  }, [terminalLines, chatDrawerOpen, remoteSessionHeight]);

  const handleRemoteSessionResize = useCallback((event: MouseEvent) => {
    if (!resizeStartRef.current) return;

    const { startY, startHeight } = resizeStartRef.current;
    const deltaY = event.clientY - startY;
    const nextHeight = Math.min(520, Math.max(220, startHeight - deltaY));
    setRemoteSessionHeight(nextHeight);
  }, []);

  const stopRemoteSessionResize = useCallback(() => {
    resizeStartRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("mousemove", handleRemoteSessionResize);
    window.removeEventListener("mouseup", stopRemoteSessionResize);
  }, [handleRemoteSessionResize]);

  const startRemoteSessionResize = (event: React.MouseEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    resizeStartRef.current = { startY: event.clientY, startHeight: remoteSessionHeight };
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", handleRemoteSessionResize);
    window.addEventListener("mouseup", stopRemoteSessionResize);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleRemoteSessionResize);
      window.removeEventListener("mouseup", stopRemoteSessionResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [handleRemoteSessionResize, stopRemoteSessionResize]);

  const handleDisconnectRemoteSession = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!aiThreadId || !userId || disconnectInFlight) return;
      setDisconnectInFlight(true);
      try {
        await chatApiInstance.terminateRemoteShellSession(String(aiThreadId), userId);
      } catch (err) {
        console.error("[RemoteSessionInputSection] Failed to disconnect remote session:", err);
      } finally {
        setDisconnectInFlight(false);
      }
    },
    [aiThreadId, userId, disconnectInFlight]
  );

  const inputBoxProps = {
    inputMessage,
    setInputMessage,
    chatIsLoading,
    aiThreadId,
    handleSendMessage,
    queuedMessages,
    onDeleteQueuedMessage,
    onUpdateQueuedMessage,
    onSendQueuedMessageNow,
    canSendQueuedMessagesNow,
    onStopTurn,
    inputRef,
    showTypeahead,
    hasNavigatedToTypeahead,
    isEmbedded,
    fontSizes,
    currentMspId,
    preloadedReportAttachments,
    preloadForThreadId,
    onPreloadedReportAttachmentsConsumed,
    modeControl,
    enableReportAttachments,
    disabledReason,
    placeholder,
  };

  if (!hasRemoteSessionDrawer) {
    return <InputBox {...inputBoxProps} />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid #D8DEE8",
        borderRadius: isEmbedded ? 2 : 4,
        boxShadow: isEmbedded
          ? "none"
          : "0 10px 24px -18px rgba(16, 24, 40, 0.45), 0 2px 6px rgba(16, 24, 40, 0.08)",
        background: "#FCFDFF",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: isEmbedded ? 2 : 3,
          py: 0.75,
          background: "linear-gradient(180deg, #FFFFFF 0%, #F9FBFF 100%)",
          borderBottom: chatDrawerOpen ? "1px solid rgba(112, 93, 72, 0.12)" : "none",
          boxShadow: chatDrawerOpen
            ? "inset 0 -1px 0 rgba(112, 93, 72, 0.08)"
            : "inset 0 -1px 0 rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
          "&:hover": { bgcolor: "rgba(247, 249, 253, 1)" },
        }}
      >
        <Typography
          onClick={() => setChatDrawerOpen((open) => !open)}
          sx={{
            fontSize: 13,
            color: "#5B6583",
            fontWeight: 500,
            letterSpacing: "0.01em",
            fontFamily: "Inter, sans-serif",
            cursor: "pointer",
            flex: 1,
            minWidth: 0,
          }}
          role="button"
          aria-expanded={chatDrawerOpen}
          aria-label={chatDrawerOpen ? "Collapse secure shell drawer" : "Expand secure shell drawer"}
        >
          Secure Shell
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <Tooltip title="Disconnect secure shell">
            <span>
              <IconButton
                size="small"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={handleDisconnectRemoteSession}
                disabled={!aiThreadId || !userId || disconnectInFlight}
                aria-label="Disconnect secure shell"
                sx={{
                  color: "#666F8D",
                  p: 0.25,
                  mr: 0.25,
                  "&:hover": { bgcolor: "rgba(102, 111, 141, 0.12)" },
                }}
              >
                {disconnectInFlight ? (
                  <CircularProgress size={18} sx={{ color: "#666F8D" }} />
                ) : (
                  <LinkOffIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            size="small"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              setChatDrawerOpen((open) => !open);
            }}
            aria-label={chatDrawerOpen ? "Collapse secure shell drawer" : "Expand secure shell drawer"}
            sx={{
              color: "#666F8D",
              p: 0.25,
              "&:hover": { bgcolor: "rgba(102, 111, 141, 0.12)" },
            }}
          >
            {chatDrawerOpen ? (
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            ) : (
              <ExpandLessIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={chatDrawerOpen} timeout="auto" unmountOnExit>
        <Box
          sx={{
            p: 1.5,
            background: "linear-gradient(180deg, #FBFCFF 0%, #FFFFFF 100%)",
            boxShadow:
              "inset 0 10px 14px -14px rgba(31, 41, 55, 0.35), inset 0 -1px 0 rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            onMouseDown={startRemoteSessionResize}
            role="separator"
            aria-label="Resize secure shell drawer"
            aria-orientation="horizontal"
            sx={{
              height: 10,
              mt: -0.5,
              mb: 1,
              mx: -0.5,
              cursor: "ns-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 3,
                borderRadius: 999,
                background: "rgba(102, 111, 141, 0.35)",
              }}
            />
          </Box>
          <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #202938",
                background: "#0F172A",
                boxShadow:
                  "0 10px 22px -16px rgba(0, 0, 0, 0.9), inset 0 1px 0 rgba(255,255,255,0.08)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.75,
                  background: "linear-gradient(180deg, #1E293B 0%, #182336 100%)",
                  borderBottom: "1px solid rgba(148, 163, 184, 0.22)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#D5DFEE",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {`theo@${remoteSession?.deviceId || "remote-host"}:~ (${remoteSession?.osType || "mac"}/${remoteSession?.shellType || "bash"})`}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#22C55E",
                      boxShadow: "0 0 8px rgba(34, 197, 94, 0.8)",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#9EE6B3",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    {remoteSessionStatusText}
                  </Typography>
                </Box>
              </Box>
              <Box
                ref={terminalOutputRef}
                sx={{
                  p: 1.5,
                  height: remoteSessionHeight,
                  fontSize: 12,
                  lineHeight: 1.6,
                  fontFamily: "Menlo, Monaco, Consolas, 'Courier New', monospace",
                  color: "#C4D0E5",
                  overflowX: "auto",
                  overflowY: "scroll",
                  scrollbarGutter: "stable",
                  scrollbarWidth: "auto",
                  scrollbarColor: "#475569 #0B1220",
                  background:
                    "radial-gradient(circle at top right, rgba(56, 189, 248, 0.12), transparent 42%), #0B1220",
                  "&::-webkit-scrollbar": {
                    width: 12,
                    height: 12,
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#0B1220",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#475569",
                    borderRadius: 999,
                    border: "2px solid #0B1220",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#64748B",
                  },
                }}
              >
                {terminalLines.length === 0 ? (
                  <Typography component="div" sx={{ font: "inherit", color: "#94A3B8" }}>
                    Waiting for secure shell events...
                  </Typography>
                ) : (
                  terminalLines.map((line: string, index: number) => (
                    <Typography
                      key={`${index}_${line.slice(0, 24)}`}
                      component="div"
                      sx={{
                        font: "inherit",
                        color: line.startsWith("$ ") ? "#93C5FD" : "#C4D0E5",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {line}
                    </Typography>
                  ))
                )}
              </Box>
          </Box>
        </Box>
      </Collapse>
      <InputBox {...inputBoxProps} attachToDrawer />
    </Box>
  );
};

export default RemoteSessionInputSection;
